/**
 * Canonical Unify date/time write + US display helpers (ENG-3287).
 *
 * Absolute instants → UTC ISO via toFhirInstant.
 * Calendar dates → YYYY-MM-DD via toFhirDateOnlyLocal / parseDateOnlyLocal.
 * Instant display → Practitioner preference, else browser IANA, else UTC + " (UTC)".
 */

import { formatDateOnlyLocal, parseDateOnlyLocal } from '../pages/public/intake/intakeDateUtils';
import { formatUsTimeZoneAbbreviation } from './usTimeZoneLabel';

export type FhirDate = string;

const FHIR_INSTANT_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:\d{2})$/;

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Absolute FHIR instant/dateTime only. Prohibited for FHIR date / floating values. */
export function toFhirInstant(d: Date): string {
  return d.toISOString();
}

/**
 * Civil calendar day in the browser's local timezone (Date#getFullYear/getMonth/getDate).
 * Callers must pass a Date whose local components already represent the intended civil day.
 */
export function toFhirDateOnlyLocal(d: Date): string {
  return formatDateOnlyLocal(d);
}

/** `YYYY-MM-DDTHH:mm` in browser-local time, for `<input type="datetime-local">` (never `toISOString().slice(0, 16)`). */
export function toDatetimeLocalValue(d: Date): string {
  return `${formatDateOnlyLocal(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export type ZonedDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

let preferredDisplayTimeZone: string | null = null;
let preferredDisplayTimeZoneGeneration = 0;
const preferredDisplayTimeZoneListeners = new Set<() => void>();

function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function emitPreferredDisplayTimeZone(): void {
  for (const listener of preferredDisplayTimeZoneListeners) listener();
}

/** Signed-in Practitioner IANA preference for UI instants. Invalid/empty clears the override. */
export function setPreferredDisplayTimeZone(iana: string | null | undefined): void {
  const trimmed = iana?.trim() ?? '';
  preferredDisplayTimeZone = trimmed && isValidIanaTimeZone(trimmed) ? trimmed : null;
  preferredDisplayTimeZoneGeneration += 1;
  emitPreferredDisplayTimeZone();
}

/** Generation token so in-flight profile reads cannot overwrite a newer save. */
export function getPreferredDisplayTimeZoneGeneration(): number {
  return preferredDisplayTimeZoneGeneration;
}

export function subscribePreferredDisplayTimeZone(onStoreChange: () => void): () => void {
  preferredDisplayTimeZoneListeners.add(onStoreChange);
  return () => {
    preferredDisplayTimeZoneListeners.delete(onStoreChange);
  };
}

export function getPreferredDisplayTimeZoneSnapshot(): string | null {
  return preferredDisplayTimeZone;
}

export function resolveDisplayTimeZone(): { timeZone: string; appendUtcLabel: boolean } {
  if (preferredDisplayTimeZone) {
    return { timeZone: preferredDisplayTimeZone, appendUtcLabel: false };
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0) {
      new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
      return { timeZone: tz, appendUtcLabel: false };
    }
  } catch {
    /* fall through */
  }
  return { timeZone: 'UTC', appendUtcLabel: true };
}

export function getZonedDateTimeParts(date: Date, timeZone: string): ZonedDateTimeParts {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    const num = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === type)?.value ?? NaN);
    let hour = num('hour');
    if (hour === 24) hour = 0;
    const year = num('year');
    const month = num('month');
    const day = num('day');
    const minute = num('minute');
    if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) {
      throw new Error('incomplete parts');
    }
    return { year, month, day, hour, minute };
  } catch {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
    };
  }
}

export function isSameZonedDay(a: Date, b: Date, timeZone: string): boolean {
  const pa = getZonedDateTimeParts(a, timeZone);
  const pb = getZonedDateTimeParts(b, timeZone);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

/** Civil Y-M-D + wall clock in `timeZone` → UTC Date (FHIR instant encoding unchanged). */
export function zonedWallClockToUtcDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const convert = (y: number, m: number, d: number, h: number, min: number): Date => {
    const desiredAsUtc = Date.UTC(y, m - 1, d, h, min, 0);
    const adjust = (ms: number): number => {
      const zoned = getZonedDateTimeParts(new Date(ms), timeZone);
      const zonedAsUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, 0);
      return ms - (zonedAsUtc - desiredAsUtc);
    };
    return new Date(adjust(adjust(desiredAsUtc)));
  };
  const matches = (date: Date, y: number, m: number, d: number, h: number, min: number): boolean => {
    const p = getZonedDateTimeParts(date, timeZone);
    return p.year === y && p.month === m && p.day === d && p.hour === h && p.minute === min;
  };

  const result = convert(year, month, day, hour, minute);
  if (matches(result, year, month, day, hour, minute)) return result;

  const parts = getZonedDateTimeParts(result, timeZone);
  const requestedMs = Date.UTC(year, month - 1, day, hour, minute);
  const gotMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  if (gotMs > requestedMs && matches(result, parts.year, parts.month, parts.day, parts.hour, parts.minute)) {
    return result;
  }
  if (gotMs < requestedMs) {
    const next = new Date(requestedMs + (requestedMs - gotMs));
    const y = next.getUTCFullYear();
    const m = next.getUTCMonth() + 1;
    const d = next.getUTCDate();
    const h = next.getUTCHours();
    const min = next.getUTCMinutes();
    const snapped = convert(y, m, d, h, min);
    if (matches(snapped, y, m, d, h, min)) return snapped;
  }
  return result;
}

export function applyZonedWallClock(
  civilDate: Date,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  return zonedWallClockToUtcDate(
    civilDate.getFullYear(),
    civilDate.getMonth() + 1,
    civilDate.getDate(),
    hour,
    minute,
    timeZone,
  );
}

const WEEKDAY_SUN0 = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Sunday = 0 in `timeZone`. */
export function getZonedWeekday(date: Date, timeZone: string): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(date);
  const index = WEEKDAY_SUN0.indexOf(name as (typeof WEEKDAY_SUN0)[number]);
  return index >= 0 ? index : 0;
}

export function addCivilDays(
  year: number,
  month: number,
  day: number,
  delta: number,
): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}

/** Midnight of the civil day containing `date` in `timeZone`. */
export function startOfZonedDay(date: Date, timeZone: string): Date {
  const p = getZonedDateTimeParts(date, timeZone);
  return zonedWallClockToUtcDate(p.year, p.month, p.day, 0, 0, timeZone);
}

export function addZonedDays(date: Date, deltaDays: number, timeZone: string): Date {
  const p = getZonedDateTimeParts(date, timeZone);
  const next = addCivilDays(p.year, p.month, p.day, deltaDays);
  return zonedWallClockToUtcDate(next.year, next.month, next.day, 12, 0, timeZone);
}

export function addZonedMonths(date: Date, deltaMonths: number, timeZone: string): Date {
  const p = getZonedDateTimeParts(date, timeZone);
  const monthIndex = p.month - 1 + deltaMonths;
  const probe = new Date(Date.UTC(p.year, monthIndex, 1));
  const year = probe.getUTCFullYear();
  const month = probe.getUTCMonth() + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return zonedWallClockToUtcDate(year, month, Math.min(p.day, lastDay), 12, 0, timeZone);
}

/** Sunday-start week of seven display-zone midnights. */
export function getZonedWeekDays(date: Date, timeZone: string): Date[] {
  const p = getZonedDateTimeParts(date, timeZone);
  const start = addCivilDays(p.year, p.month, p.day, -getZonedWeekday(date, timeZone));
  return Array.from({ length: 7 }, (_, i) => {
    const d = addCivilDays(start.year, start.month, start.day, i);
    return zonedWallClockToUtcDate(d.year, d.month, d.day, 0, 0, timeZone);
  });
}

/** Six-week month grid starting on the Sunday before the 1st (display zone). */
export function getZonedMonthGridDays(date: Date, timeZone: string): Date[] {
  const p = getZonedDateTimeParts(date, timeZone);
  const first = zonedWallClockToUtcDate(p.year, p.month, 1, 0, 0, timeZone);
  const start = addCivilDays(p.year, p.month, 1, -getZonedWeekday(first, timeZone));
  return Array.from({ length: 42 }, (_, i) => {
    const d = addCivilDays(start.year, start.month, start.day, i);
    return zonedWallClockToUtcDate(d.year, d.month, d.day, 0, 0, timeZone);
  });
}

/** Wall clock on the civil day of `dayInstant` in `timeZone` (grid columns). */
export function applyZonedWallClockOnZonedDay(
  dayInstant: Date,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const p = getZonedDateTimeParts(dayInstant, timeZone);
  return zonedWallClockToUtcDate(p.year, p.month, p.day, hour, minute, timeZone);
}

function calendarPartsFromValue(value: FhirDate | Date): { y: number; m: number; d: number } | null {
  if (typeof value === 'string') {
    const parsed = parseDateOnlyLocal(value);
    if (!parsed) return null;
    return { y: parsed.getFullYear(), m: parsed.getMonth() + 1, d: parsed.getDate() };
  }
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
}

function ordinalSuffix(day: number): string {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function isValidCivilYmd(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** Parse ISO instant with mandatory Z or numeric offset; reject naive/date-only/invalid. */
export function parseStrictFhirInstant(value: string): Date | null {
  const m = FHIR_INSTANT_RE.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = m[6] != null ? Number(m[6]) : 0;
  if (!isValidCivilYmd(y, mo, day)) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const offset = m[8];
  if (offset !== 'Z') {
    const om = /^([+-])(\d{2}):(\d{2})$/.exec(offset);
    if (!om) return null;
    const oh = Number(om[2]);
    const omin = Number(om[3]);
    if (oh > 14 || omin > 59) return null;
  }
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms);
}

/**
 * For fields that may hold a FHIR `date` or an instant/dateTime (birthDate, due dates, mock rows).
 * `YYYY-MM-DD` → local midnight of that civil day (never UTC midnight); anything else → Date.parse.
 * Returns null for empty/unparseable input.
 */
export function parseFhirDateOrInstant(value: string | null | undefined): Date | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return parseDateOnlyLocal(t);
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? null : new Date(ms);
}

function coerceInstant(value: Date | string): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string' || !value.trim()) return null;
  return parseStrictFhirInstant(value);
}

function formatInstantParts(d: Date, timeZone: string, seconds = false): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' as const } : {}),
    hour12: true,
  }).format(d);
}

/** "Nov 12, 2000" */
export function formatUsDateShort(value: FhirDate | Date): string {
  const p = calendarPartsFromValue(value);
  if (!p) return '';
  return `${SHORT_MONTHS[p.m - 1]} ${p.d}, ${p.y}`;
}

/** "11-12-2000" */
export function formatUsDateNumeric(value: FhirDate | Date): string {
  const p = calendarPartsFromValue(value);
  if (!p) return '';
  return `${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}-${p.y}`;
}

/** "11-12" */
export function formatUsDateMonthDay(value: FhirDate | Date): string {
  const p = calendarPartsFromValue(value);
  if (!p) return '';
  return `${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/** "Nov 12th" */
export function formatUsDateMonthDayOrdinal(value: FhirDate | Date): string {
  const p = calendarPartsFromValue(value);
  if (!p) return '';
  return `${SHORT_MONTHS[p.m - 1]} ${p.d}${ordinalSuffix(p.d)}`;
}

/** Format with a pre-resolved display zone (tests + formatUsDateTime). */
export function formatUsDateTimeResolved(
  value: Date | string,
  resolved: { timeZone: string; appendUtcLabel: boolean; seconds?: boolean },
): string {
  const instant = coerceInstant(value);
  if (!instant) return '';
  const formatted = formatInstantParts(instant, resolved.timeZone, resolved.seconds === true);
  return resolved.appendUtcLabel ? `${formatted} (UTC)` : formatted;
}

/**
 * Instants only. Explicit timeZone skips the (UTC) fallback label.
 * Without timeZone: Practitioner preference, else browser IANA, else UTC + " (UTC)".
 */
export function formatUsDateTime(value: Date | string, timeZone?: string): string {
  if (timeZone !== undefined) {
    return formatUsDateTimeResolved(value, { timeZone, appendUtcLabel: false });
  }
  return formatUsDateTimeResolved(value, resolveDisplayTimeZone());
}

/**
 * Instant display with a compact US zone abbreviation (PT, MT, CT, ET, …).
 * UTC fallback already includes ` (UTC)` and is left unchanged.
 */
export function formatUsDateTimeWithZone(value: Date | string, timeZone?: string): string {
  if (timeZone !== undefined) {
    const formatted = formatUsDateTimeResolved(value, { timeZone, appendUtcLabel: false });
    if (!formatted) return '';
    const abbr = formatUsTimeZoneAbbreviation(timeZone);
    return abbr ? `${formatted} ${abbr}` : formatted;
  }
  const resolved = resolveDisplayTimeZone();
  const formatted = formatUsDateTimeResolved(value, resolved);
  if (!formatted) return '';
  if (resolved.appendUtcLabel) return formatted;
  const abbr = formatUsTimeZoneAbbreviation(resolved.timeZone);
  return abbr ? `${formatted} ${abbr}` : formatted;
}

/** Time-only range in browser/explicit zone (e.g. "1:00 PM - 2:00 PM"). */
export function formatUsTimeRange(
  start: string | undefined,
  end: string | undefined,
  timeZone?: string,
): string {
  if (!start) return '';
  const resolved =
    timeZone !== undefined
      ? { timeZone, appendUtcLabel: false }
      : resolveDisplayTimeZone();
  const startInstant = parseStrictFhirInstant(start);
  if (!startInstant) return '';
  const fmt = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: resolved.timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  const startLabel = fmt(startInstant);
  if (!end) {
    return resolved.appendUtcLabel ? `${startLabel} (UTC)` : startLabel;
  }
  const endInstant = parseStrictFhirInstant(end);
  if (!endInstant) {
    return resolved.appendUtcLabel ? `${startLabel} (UTC)` : startLabel;
  }
  const range = `${startLabel} - ${fmt(endInstant)}`;
  return resolved.appendUtcLabel ? `${range} (UTC)` : range;
}
